import React, { useState } from 'react';
import { EQUIPMENT_TYPES } from '../constants/equipmentTypes';

export default function RateCalculator({ initialData, onRateSelected }) {
  const [formData, setFormData] = useState({
    originState: initialData?.originState || '',
    destState: initialData?.destState || '',
    distanceMiles: initialData?.distanceMiles || '',
    equipmentType: initialData?.equipmentType || 'DRY_VAN',
    commodity: initialData?.commodity || '',
    weightLbs: initialData?.weightLbs || '',
    pickupDate: initialData?.pickupStart ? initialData.pickupStart.split('T')[0] : '',
  });

  const [recommendation, setRecommendation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleCalculate(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch('/api/rates/calculate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to calculate rate');
      }

      const data = await response.json();
      setRecommendation(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleUseRate(rateCents) {
    if (onRateSelected) {
      onRateSelected(rateCents);
    }
  }

  return (
    <div style={{ background: '#f9fafb', padding: 20, borderRadius: 8, border: '1px solid #e5e7eb' }}>
      <h3 style={{ margin: '0 0 16px 0', fontSize: 18, fontWeight: 700 }}>
        💰 Rate Calculator
      </h3>

      <form onSubmit={handleCalculate} style={{ marginBottom: 20 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
              Origin State *
            </label>
            <input
              type="text"
              maxLength="2"
              value={formData.originState}
              onChange={(e) => setFormData({ ...formData, originState: e.target.value.toUpperCase() })}
              required
              placeholder="TX"
              style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #d1d5db' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
              Dest State *
            </label>
            <input
              type="text"
              maxLength="2"
              value={formData.destState}
              onChange={(e) => setFormData({ ...formData, destState: e.target.value.toUpperCase() })}
              required
              placeholder="CA"
              style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #d1d5db' }}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
              Distance (miles) *
            </label>
            <input
              type="number"
              value={formData.distanceMiles}
              onChange={(e) => setFormData({ ...formData, distanceMiles: e.target.value })}
              required
              placeholder="1250"
              style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #d1d5db' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
              Equipment Type *
            </label>
            <select
              value={formData.equipmentType}
              onChange={(e) => setFormData({ ...formData, equipmentType: e.target.value })}
              style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #d1d5db' }}
            >
              {EQUIPMENT_TYPES.map((et) => (
                <option key={et.value} value={et.value}>
                  {et.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
              Weight (lbs)
            </label>
            <input
              type="number"
              value={formData.weightLbs}
              onChange={(e) => setFormData({ ...formData, weightLbs: e.target.value })}
              placeholder="42000"
              style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #d1d5db' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
              Pickup Date
            </label>
            <input
              type="date"
              value={formData.pickupDate}
              onChange={(e) => setFormData({ ...formData, pickupDate: e.target.value })}
              style={{ width: '100%', padding: 8, borderRadius: 4, border: '1px solid #d1d5db' }}
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%',
            padding: '10px 16px',
            background: loading ? '#9ca3af' : '#DC2626',
            color: 'white',
            border: 'none',
            borderRadius: 6,
            fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? 'Calculating...' : 'Calculate Rate'}
        </button>
      </form>

      {error && (
        <div style={{ padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#dc2626', fontSize: 14, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {recommendation && (
        <div>
          <div style={{ background: 'white', padding: 16, borderRadius: 8, border: '2px solid #10b981', marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
              💡 Recommended Rate
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 24, fontWeight: 900, color: '#10b981' }}>
                  ${recommendation.recommended.avgRate}
                </div>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                  ${recommendation.recommended.perMile}/mile • Range: ${recommendation.recommended.lowRate} - ${recommendation.recommended.highRate}
                </div>
              </div>
              {onRateSelected && (
                <button
                  onClick={() => handleUseRate(recommendation.recommended.avgRateCents)}
                  style={{
                    padding: '8px 16px',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontSize: 14,
                  }}
                >
                  Use This Rate
                </button>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
            <div style={{ background: 'white', padding: 12, borderRadius: 6, border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Low</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>${recommendation.recommended.lowRate}</div>
            </div>
            <div style={{ background: 'white', padding: 12, borderRadius: 6, border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>Market Avg</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>${recommendation.recommended.avgRate}</div>
            </div>
            <div style={{ background: 'white', padding: 12, borderRadius: 6, border: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>High</div>
              <div style={{ fontSize: 16, fontWeight: 700 }}>${recommendation.recommended.highRate}</div>
            </div>
          </div>

          <div style={{ fontSize: 11, color: '#6b7280', display: 'flex', justifyContent: 'space-between' }}>
            <span>Confidence: {(recommendation.confidence * 100).toFixed(0)}%</span>
            {recommendation.historical && (
              <span>Based on {recommendation.historical.sampleSize} recent loads</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
